import { ReactNode } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Textarea } from "../components/ui/textarea"
import { Label } from "../components/ui/label"
import { Mail, MessageSquare, Clock, MapPin } from "lucide-react"
import Layout from "../layouts/Layout"
import { useForm } from "@inertiajs/react"
import FlashMessages from "../components/FlashMessages"
import { Head } from "@inertiajs/react"

const Contact = () => {
  const { data, setData, post, processing, errors } = useForm({
    name: "",
    email: "",
    subject: "",
    message: ""
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    post('/contacts', {
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        setData("name", "")
        setData("email", "")
        setData("subject", "")
        setData("message", "")
      }
    })
  }

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      description: "codeware.feedback@gmail.com",
      action: "mailto:codeware.feedback@gmail.com"
    },
    {
      icon: MessageSquare,
      title: "Response Time",
      description: "Within 24 hours",
      action: null
    },
    {
      icon: Clock,
      title: "Availability",
      description: "Mon-Fri, 9AM-6PM EET",
      action: null
    },
    {
      icon: MapPin,
      title: "Location",
      description: "Estonia",
      action: null
    }
  ]

  const projectTypes = [
    {
      title: "Rails Development",
      description: "Full-stack applications, APIs, and system integrations",
      examples: ["E-commerce platforms", "SaaS applications", "API development", "Legacy modernization"]
    },
    {
      title: "AI Integration",
      description: "Machine learning features and AI-powered solutions",
      examples: ["Content generation", "Data analysis", "Automation tools", "ML prototypes"]
    },
    {
      title: "System Integration",
      description: "Connecting different systems and APIs",
      examples: ["API integrations", "Third-party services", "Data synchronization", "Microservices architecture"]
    },
    {
      title: "Technical Writing",
      description: "Documentation and content creation",
      examples: ["API documentation", "Technical blogs", "System guides", "Code reviews"]
    }
  ]

  return (
    <>
      <Head title="Codeware - Contact" >
        <meta name="description" content="Contact Codeware for Rails development, AI integration, and domain industry expertise." />
      </Head>

      <FlashMessages />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">Get In Touch</h1>
          <p className="text-xl text-muted-foreground mb-8">
            Ready to start your next Rails project, AI integration, or need domain industry expertise? 
            Let's discuss how I can help bring your ideas to life. If you're interested in joining me on projects, feel free to reach out as well.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Send a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and I'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={data.name}
                        onChange={(e) => setData("name", e.target.value)}
                        required
                        placeholder="Your name"
                        className={errors.name ? "border-red-500" : ""}
                      />
                      {errors.name && (
                        <p className="text-sm text-red-500">{errors.name}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={data.email}
                        onChange={(e) => setData("email", e.target.value)}
                        required
                        placeholder="your.email@example.com"
                        className={errors.email ? "border-red-500" : ""}
                      />
                      {errors.email && (
                        <p className="text-sm text-red-500">{errors.email}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={data.subject}
                      onChange={(e) => setData("subject", e.target.value)}
                      required
                      placeholder="Brief description of your project"
                      className={errors.subject ? "border-red-500" : ""}
                    />
                    {errors.subject && (
                      <p className="text-sm text-red-500">{errors.subject}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={data.message}
                      onChange={(e) => setData("message", e.target.value)}
                      required
                      rows={6}  
                      placeholder="Tell me about your project, timeline, budget, and any specific requirements..."
                      className={errors.message ? "border-red-500" : ""}
                    />
                    {errors.message && (
                      <p className="text-sm text-red-500">{errors.message}</p>
                    )}
                  </div>
                  <Button type="submit" size="lg" disabled={processing} className="w-full sm:w-auto">
                    {processing ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info & Project Types */}
          <div className="space-y-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {contactInfo.map((info) => {
                  const IconComponent = info.icon;
                  const content = (
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <IconComponent className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{info.title}</p>
                        <p className="text-xs text-muted-foreground">{info.description}</p>
                      </div>
                    </div>
                  );

                  return info.action ? (
                    <a key={info.title} href={info.action} className="block hover:bg-muted/50 py-2 rounded-lg transition-colors">
                      {content}
                    </a>
                  ) : (
                    <div key={info.title}>{content}</div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card>
              <CardHeader>
                <CardTitle>Connect Online</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <a 
                  href="https://github.com/maricavor" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded-lg transition-colors"
                >
                  <div className="p-2 rounded-lg bg-primary/10">
                    <svg className="h-4 w-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">GitHub</p>
                    <p className="text-xs text-muted-foreground">View my code</p>
                  </div>
                </a>
                <a 
                  href="https://www.linkedin.com/in/sergey-tsyganov" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded-lg transition-colors"
                >
                  <div className="p-2 rounded-lg bg-primary/10">
                    <svg className="h-4 w-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">LinkedIn</p>
                    <p className="text-xs text-muted-foreground">Professional profile</p>
                  </div>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Project Types */}
        <section className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Project Types</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Here are some examples of projects I typically work on. Don't see your specific need? 
              Feel free to reach out anyway!
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {projectTypes.map((type) => (
              <Card key={type.title} className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg">{type.title}</CardTitle>
                  <CardDescription>{type.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {type.examples.map((example) => (
                      <li key={example} className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-primary"></div>
                        {example}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      </div>

    </>
  )
}

export default Contact

Contact.layout = (page: ReactNode) => <Layout>{page}</Layout>